import React, { useState, useMemo } from 'react';
import PersonCard from './PersonCard';

const CLASS_TYPES = [
  { key: 'reg_discovery', label: 'Discovery 101', className: 'discovery' },
  { key: 'reg_serve', label: 'Serve 201', className: 'serve' },
  { key: 'reg_values', label: 'Values 301', className: 'values' },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  const clean = dateStr.split('T')[0];
  const parts = clean.includes('/') ? clean.split('/') : clean.split('-');
  let d;
  if (parts[0].length === 4) {
    d = new Date(parts[0], parts[1] - 1, parts[2]);
  } else {
    d = new Date(parts[2], parts[0] - 1, parts[1]);
  }
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function parseDateForSort(dateStr) {
  if (!dateStr) return null;
  const clean = dateStr.split('T')[0];
  const parts = clean.includes('/') ? clean.split('/') : clean.split('-');
  if (parts[0].length === 4) {
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  return new Date(parts[2], parts[0] - 1, parts[1]);
}

function UpcomingClassView({ allPeople, onPersonClick }) {
  const [selectedClass, setSelectedClass] = useState('reg_discovery');
  const [selectedDate, setSelectedDate] = useState(null);

  const classType = CLASS_TYPES.find(c => c.key === selectedClass);

  // Get upcoming dates (today or future) for the selected class, sorted soonest-first
  const upcomingDates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateSet = new Map();
    for (const p of allPeople) {
      const dateVal = p[classType.key];
      if (dateVal) {
        const parsed = parseDateForSort(dateVal);
        if (parsed && parsed >= today) {
          const key = parsed.toISOString().split('T')[0];
          if (!dateSet.has(key)) {
            dateSet.set(key, { raw: dateVal, parsed, key });
          }
        }
      }
    }
    return Array.from(dateSet.values()).sort((a, b) => a.parsed - b.parsed);
  }, [allPeople, classType.key]);

  // Reset selected date when switching class type if it's not valid
  const activeDate = upcomingDates.find(d => d.key === selectedDate) ? selectedDate : null;

  // Filter people for the selected date
  const filteredPeople = useMemo(() => {
    if (!activeDate) return [];
    const people = allPeople.filter(p => {
      const dateVal = p[classType.key];
      if (!dateVal) return false;
      const parsed = parseDateForSort(dateVal);
      if (!parsed) return false;
      return parsed.toISOString().split('T')[0] === activeDate;
    });
    people.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
    return people;
  }, [allPeople, activeDate, classType.key]);

  return (
    <div className="class-view">
      <div className="class-view-controls">
        <div className="class-type-tabs">
          {CLASS_TYPES.map(ct => (
            <button
              key={ct.key}
              className={`class-type-tab ${ct.className}${selectedClass === ct.key ? ' active' : ''}`}
              onClick={() => { setSelectedClass(ct.key); setSelectedDate(null); }}
            >
              {ct.label}
            </button>
          ))}
        </div>

        {upcomingDates.length > 0 && (
          <div className="class-date-selector">
            <select
              value={activeDate || ''}
              onChange={e => setSelectedDate(e.target.value || null)}
            >
              <option value="">Select an upcoming date...</option>
              {upcomingDates.map(d => (
                <option key={d.key} value={d.key}>{formatDate(d.raw)}</option>
              ))}
            </select>
          </div>
        )}

        {activeDate && (
          <span className="class-view-count">
            {filteredPeople.length} {filteredPeople.length === 1 ? 'person' : 'people'} registered
          </span>
        )}
      </div>

      {!activeDate && upcomingDates.length > 0 && (
        <div className="class-view-empty">
          Select a date to view registrations
        </div>
      )}

      {upcomingDates.length === 0 && (
        <div className="class-view-empty">
          No upcoming {classType.label} registrations
        </div>
      )}

      {activeDate && filteredPeople.length > 0 && (
        <div className="host-groups">
          <div className="host-group">
            <div className="host-group-header">
              <span className={`host-name-tag ${classType.className}`}>{formatDate(upcomingDates.find(d => d.key === activeDate)?.raw)}</span>
              <span className="host-group-count">
                {filteredPeople.length} {filteredPeople.length === 1 ? 'person' : 'people'}
              </span>
            </div>
            <div className="section-divider" />
            <div className="people-grid">
              {filteredPeople.map(person => (
                <PersonCard
                  key={person.id}
                  person={person}
                  sectionClass={classType.className}
                  onClick={() => onPersonClick(person)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { UpcomingClassView };
